import type { PatchOp } from "./patch";
import type { STATE_KEYS } from "./tools/giraffe";

/**
 * The records `giraffe_sdk_patch` may change, each paired with the state key
 * that reads it and the procedure that writes it back (#41).
 *
 * **One entry per KIND of record, never per use case.** Editing a measure,
 * renaming a usage or re-pointing an iframe are all recipes over `projectApp`.
 * A new entry is needed only for a new kind of record the host writes whole.
 *
 * The pairing is decided here and not by the agent: an agent free to choose the
 * read and the write separately could pair a read with the wrong write, which
 * is the damage this primitive exists to prevent.
 *
 * ## Provenance, and why P1 applies
 *
 * `projectApp` rests on `updateProjectApp`'s replace semantics, *verified
 * against the current build* at `giraffe-four@37a2bb94a` (2026-09-21):
 * `projectAppToProjectAppEdit` (`project-core/src/utils.ts:240`) drops the
 * stored `public` and `featureCategories` on every write and keeps only what
 * was sent. That is current-build behaviour and not a contract. If it changes,
 * a patch is still safer than a raw write, but this guarantee needs re-checking.
 *
 * ## The frame reads the host's view, not the stored record
 *
 * What the frame reads is the HOST'S VIEW: defaults merged in, nulls stripped at
 * every depth, and for apps other than "1" reduced to `{ public }` (*verified
 * against the current build*, `giraffe-four@37a2bb94a` (2026-09-21):
 * `ProjectStateManagerBase.ts:284-295`, `project-core/src/utils.ts`
 * `cloneDeepWithoutNull`). Any new entry must answer whether its read is the
 * whole record, and list in `ids` only the records where it is.
 */

type StateKey = (typeof STATE_KEYS)[number];

type PatchableEntry = {
  readonly stateKey: StateKey;
  readonly writeFn: string;
  /** The record ids a patch may name; `null` means any. */
  readonly ids: readonly string[] | null;
};

export const PATCHABLE = {
  projectApp: {
    stateKey: "projectAppsByAppID",
    writeFn: "updateProjectApp",
    // Every app but "1" arrives stripped to `{ public }`, so its record cannot be
    // read whole and a write would replace the rest blind. The spec keeps v1 to
    // the analytics on app "1".
    ids: ["1"],
  },
} as const satisfies Readonly<Record<string, PatchableEntry>>;

export type PatchableTarget = keyof typeof PATCHABLE;

export const PATCHABLE_TARGETS: readonly PatchableTarget[] = Object.keys(
  PATCHABLE,
) as PatchableTarget[];

export function isPatchableTarget(value: unknown): value is PatchableTarget {
  return typeof value === "string" && Object.hasOwn(PATCHABLE, value);
}

/**
 * What the relay sends the frame in place of the agent's arguments. The frame
 * trusts it and does not re-check `PATCHABLE`: one copy, at the relay, for the
 * reason `bridge/hostTools.ts` gives for the invoke checks.
 */
export type PatchPlan = {
  readonly stateKey: string;
  readonly id: string;
  readonly writeFn: string;
  readonly ops: readonly PatchOp[];
};
